import {useState} from 'react';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Textarea} from '@common-web/components/ui/textarea';
import {Checkbox} from '@common-web/components/ui/checkbox';
import {trpc} from '@common-web/lib/trpc';
import {useToast} from '@common-web/hooks/use-toast';
import {MemberLifecycleDialogFrame} from '@client/pages/client/member-profile/MemberLifecycleDialogFrame';

interface ReinstateMemberDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  groupId: string;
  memberId: string; // composed `${subscriberId}-${memberSeq}`
  memberName: string;
  /**
   * Reinstating a single dependent rather than the subscriber. Forces
   * `reenrollFamily: false` and hides the family checkbox, since reenrolling
   * the whole household from a dependent row is never the intent.
   */
  dependentOnly?: boolean;
}

const today = () => new Date().toISOString().slice(0, 10);

export function ReinstateMemberDialog({
  open,
  onClose,
  onSuccess,
  groupId,
  memberId,
  memberName,
  dependentOnly = false,
}: ReinstateMemberDialogProps) {
  const {toast} = useToast();
  const [reenrollDate, setReenrollDate] = useState<string>(today());
  const [reenrollFamily, setReenrollFamily] = useState(!dependentOnly);
  const [notes, setNotes] = useState('');

  const reactivate = trpc.group.vbaMembers.reactivate.useMutation({
    onSuccess: () => {
      toast({title: 'Member reinstated', description: `${memberName} has been reenrolled.`});
      onSuccess();
      onClose();
    },
    onError: (err) =>
      toast({title: 'Reinstatement failed', description: err.message, variant: 'destructive'}),
  });

  const handleSubmit = () => {
    reactivate.mutate({
      groupId,
      id: memberId,
      reenrollDate,
      reenrollFamily: dependentOnly ? false : reenrollFamily,
      notes: notes.trim() || null,
    });
  };

  return (
    <MemberLifecycleDialogFrame
      open={open}
      onClose={onClose}
      title={`Reinstate ${memberName}`}
      description={
        dependentOnly
          ? 'Reverse the disenrollment and restore active coverage for this dependent. The rest of the household is unaffected. This writes to VBA and is logged.'
          : 'Reverse the disenrollment and restore active coverage. This writes to VBA and is logged.'
      }
      cancelDisabled={reactivate.isPending}
      submitDisabled={reactivate.isPending}
      submitLabel={
        reactivate.isPending
          ? 'Reinstating…'
          : dependentOnly
            ? 'Reinstate dependent'
            : 'Reinstate member'
      }
      onSubmit={handleSubmit}
    >
      <div className='space-y-1.5'>
        <Label htmlFor='reenrollDate'>Reinstatement effective date</Label>
        <Input
          id='reenrollDate'
          type='date'
          value={reenrollDate}
          onChange={(e) => setReenrollDate(e.target.value)}
        />
      </div>

      {!dependentOnly && (
        <div className='flex items-center gap-2'>
          <Checkbox
            id='reenrollFamily'
            checked={reenrollFamily}
            onCheckedChange={(c) => setReenrollFamily(Boolean(c))}
          />
          <Label htmlFor='reenrollFamily' className='font-normal'>
            Reinstate entire family (subscriber + dependents)
          </Label>
        </div>
      )}

      <div className='space-y-1.5'>
        <Label htmlFor='notes'>Notes (optional)</Label>
        <Textarea
          id='notes'
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder='Internal note for this reinstatement…'
        />
      </div>
    </MemberLifecycleDialogFrame>
  );
}
